import React, { useEffect, useState } from "react";
import { diff } from "deep-diff";
import { cqAPI, getBid } from "../../lib/Auth";
import dialogManager from "../DialogManager/DialogManager";
import _ from "lodash";
import {
  Box,
  Flex,
  Button,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  useColorMode,
  Checkbox,
  IconButton,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
  SimpleGrid,
  Text,
  useTheme,
  useColorModeValue,
  Spacer,
  Link,
  Tooltip,
  Spinner
} from "@chakra-ui/react";
import ReactSelect from "react-select";
import EditableText from "../EditableText/EditableText";
import {WORKER_CARESTACK, WORKER_CLOUD9, WORKER_DENTICON, WORKER_DENTRIX, WORKER_DENTRIXASCEND} from "../AdapterSetting/constants";
import {
  AddIcon,
  DeleteIcon,
  CheckIcon,
  ExternalLinkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";

const nonConfigurablePatientTypeForServices = ["0", "1"];
const nonConfigurableServices = ["1"];
const requiresPrimaryProvider = ["0"];

const BookingSettings = () => {
  const [settings, setSettings] = useState({
    non_standard_services: [],
    service_links: [],
    employees: [],
    options: {},
  });
  const { colorMode } = useColorMode();
  const [originalSettings, setOriginalSettings] = useState({
    non_standard_services: [],
    service_links: [],
    employees: [],
    options: {},
  });

  const Hours = [
    7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5,
    15, 15.5, 16, 16.5, 17, 17.5, 18, 18.5, 19, 19.5,
  ];
  const days = ["S", "M", "T", "W", "Th", "F", "S"];
  const theme = useTheme();
  const customStyles = {
    clearIndicator: (provided) => ({
      ...provided,
      display: "none",
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.gray[100],
      borderColor: theme.colors.gray[300],
      boxShadow: theme.shadows.sm,
      "&:hover": {
        borderColor: theme.colors.gray[400],
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.gray[50],
      width: "max-content",
      minWidth: "100%",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.colors.blue[100]
        : theme.colors.gray[50],
      color: theme.colors.gray[700],
      "&:hover": {
        backgroundColor: theme.colors.blue[50],
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      width: "200px",
    }),
  };
  const [staff, setStaff] = useState([]);
  const toast = useToast();
  const {
    isOpen: isNewServiceModalOpen,
    onOpen: onOpenNewService,
    onClose: onCloseNewService,
  } = useDisclosure();

  // Activation modal states
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);
  const [activateEmployee, setActivateEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [newService, setNewService] = useState({
    name: "",
    duration: 30,
    patient_type: "n",
    description: "",
    appointment_notes: "",
  });

  const [isDenticon, setIsDenticon] = useState(false);
  const [isDentrix, setIsDentrix] = useState(false);
  const [isCloud9, setIsCloud9] = useState(false);
  const [isCareStack, setIsCareStack] = useState(false);
  const [isDentrixAscend, setIsDentrixAscend] = useState(false);

  // Per-service Advanced toggle
  const [advancedOpen, setAdvancedOpen] = useState({});

  const toggleAdvanced = (serviceId) => {
    setAdvancedOpen((prev) => ({
      ...prev,
      [serviceId]: !prev[serviceId],
    }));
  };

  const fetchSettings = async () => {
    const response = await cqAPI.get("/settings/booking");
    if (response?.type === "ok") {
      switch (response.data.businesses[0]?.pms_type) {
        case WORKER_DENTICON:
          setIsDenticon(true);
          setIsDentrix(false);
          setIsCloud9(false);
          setIsCareStack(false);
          setIsDentrixAscend(false);
          break;
        case WORKER_DENTRIX:
          setIsDenticon(false);
          setIsDentrix(true);
          setIsCloud9(false);
          setIsCareStack(false);
          setIsDentrixAscend(false);
          break;
        case WORKER_CLOUD9:
          setIsDenticon(false);
          setIsDentrix(false);
          setIsCloud9(true);
          setIsCareStack(false);
          setIsDentrixAscend(false);
          break;
        case WORKER_CARESTACK:
          setIsDenticon(false);
          setIsDentrix(false);
          setIsCloud9(false);
          setIsCareStack(true);
          setIsDentrixAscend(false);
          break;
        case WORKER_DENTRIXASCEND:
          setIsDenticon(false);
          setIsDentrix(false);
          setIsCloud9(false);
          setIsCareStack(false);
          setIsDentrixAscend(true);
          break;
        default:
          setIsDenticon(false);
          setIsDentrix(false);
          setIsCloud9(false);
          setIsCareStack(false);
          setIsDentrixAscend(false);
      }
      setSettings(response.data);
      setStaff(response.data.staff);
      setOriginalSettings(_.cloneDeep(response.data));
    }
  };

  useEffect(() => {
    fetchSettings().catch(console.error);
  }, []);

  // ----------------------------------
  // Show the activation modal
  // ----------------------------------
  const handleActivateClick = (employee) => {
    setActivateEmployee(employee);
    setIsActivateModalOpen(true);
  };

  // ----------------------------------
  // Validate email if needed
  // ----------------------------------
  const isValidEmail = (email) => {
    // Very basic email pattern check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // ----------------------------------
  // Activation logic (called by child modal)
  // ----------------------------------
  const handleActivateProviderDirect = async ({ name, email, doesNotNeedLogin }) => {
    if (!activateEmployee) return;
    const payload = {
      provider_id: activateEmployee.provider_info[getBid()].pms_provider_id,
      name: name.trim(),
      email: doesNotNeedLogin ? undefined : email,
    };
    setLoading(true)
    const resp = await cqAPI.post("/settings/booking/provider/activate", payload);
    setLoading(false)
    if (!resp) {
      // if there's no response or it's undefined
      return;
    }
    if (resp.type !== "ok") {
      toast({
        title: "Error",
        description: resp.msg || "Could not activate provider",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } else {
      await fetchSettings();
      toast({
        title: "Provider Activated",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setIsActivateModalOpen(false);
    }
  };

  const updateOption = async (field, value) => {
    const resp = await cqAPI.put("/settings/booking", { field, value });
    if (resp.msg) {
      dialogManager.open({
        onDeny: () => {},
        acceptText: "OK",
        body: resp.msg,
        header: "Error",
      });
      return false;
    }
    const f = field.includes('.')? field.split('.')[1] : field
    setSettings({
      ...settings,
      options: { ...settings.options, [f]: value },
    });
    setOriginalSettings({
      ...originalSettings,
      options: { ...originalSettings.options, [f]: value },
    });
    return true;
  };

  const updateService = async (service, field, value) => {
    const resp = await cqAPI.put("/settings/booking/service", {
      field: field,
      value: value,
      sid: service.id,
    });
    if (resp?.type === "ok") {
      setSettings({
        ...settings,
        non_standard_services: settings.non_standard_services.map((x) => {
          if (x.id === service.id) {
            x[field] = value;
          }
          return x;
        }),
        root_services: settings.root_services.map(x => {
          if (x.id === service.id) {
            x[field] = value;
          }
          return x;
        })
      });
      if (service.id === '3') {
        if ((field === 'enabled' && !value) || (field === 'patient_type' && value === 'e')) {
          await updateOption("new_patient_followed_by_hygiene", false);
        }
      }
      return true;
    }
    return false;
  };

  const handleAppointmentTypeChange = async (service, selectedTypes) => {
    if (!selectedTypes) {
      return;
    }
    const newTypes = selectedTypes.map((o) => o.value);
       const isAlwaysEnabled = nonConfigurableServices.includes(service.id);
    const isEnabled = service?.enabled;

    // user can't delete all appointment types of a service if it's enabled or always enabled
    if ((isAlwaysEnabled || isEnabled) && newTypes.length === 0) {
      toast({
        title: "Cannot remove all appointment types",
        description:
          "This service is enabled, so it must have at least one appointment type.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    await updateService(service, "appointment_types", newTypes);
  }

  const handleToggleEnabled = async (service, shouldEnable) => {
    // don't enable a service if the appointment types for it are empty
    const hasTypes = Array.isArray(service.appointment_types) && service.appointment_types.length > 0;
    if (shouldEnable && !hasTypes) {
      toast({
        title: "Appointment Type is mandatory",
        description: "If you do not see the appointment type that you need, you can create a custom type in your PMS (patient management system) and it will be added here.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    await updateService(service, "enabled", shouldEnable)
  }

  const saveNewService = async () => {
    const resp = await cqAPI.post("/settings/booking/service", newService);
    if (resp.type === "ok") {
      await fetchSettings();
      onCloseNewService();
    }
    if (resp.msg) {
      toast({
        title: "Error",
        description: resp.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleNewServiceChange = (field, value) => {
    setNewService((prev) => {
      return {
        ...prev,
        [field]: value
      }
    });
  };

  const deleteService = async (sid) => {
    const resp = await cqAPI.del("/settings/booking/service", { sid });
    if (resp.type === "ok") {
      setSettings({
        ...settings,
        non_standard_services: settings.non_standard_services.filter((x) => {
          return x.id !== sid;
        })
      });
    }
  };

  const handleServiceChange = (_employee, values) => {
    const newSettings = {
      ...settings,
      employees: settings.employees.map((employee) => {
        if (employee._id && employee._id === _employee._id) {
          employee.provider_info[settings.root_id] =
            employee.provider_info[settings.root_id] || {};
          employee.provider_info[settings.root_id].service_id = values;
        }
        return employee;
      }),
    };
    setSettings(newSettings);
    updateStaff(newSettings);
  };

  const handlePMSProviderChange = (_employee, value) => {
    const newSettings = {
      ...settings,
      employees: settings.employees.map((employee) => {
        if (employee?._id === _employee._id) {
          employee.provider_info[settings.root_id] =
            employee.provider_info[settings.root_id] || {};
          employee.provider_info[settings.root_id].pms_provider_id = value;
        }
        return employee;
      }),
    };
    setSettings(newSettings);
    updateStaff(newSettings);
  };

  const handleOperatoryChange = (_employee, newPatient, values) => {
    const newSettings = {
      ...settings,
      employees: settings.employees.map((employee) => {
        if (employee._id && employee._id === _employee._id) {
          employee.provider_info[settings.root_id] =
            employee.provider_info[settings.root_id] || {};
          if (newPatient) {
            employee.provider_info[bid].new_patient_operatory_ids =
              values;
          } else {
            employee.provider_info[bid].existing_patient_operatory_ids =
              values;
          }
        }
        return employee;
      }),
    };
    setSettings(newSettings);
    updateStaff(newSettings);
  };

  const handleTimeAdjustmentFactorChange = async (employee_id, newValue) => {
    const num = Number(newValue);

    if (Number.isNaN(num)) {
      toast({
        title: "Invalid value",
        description: "Please enter a number between 0 and 200.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    if (num < 0 || num > 200) {
      toast({
        title: "Out of range",
        description: "Time Adjustment Factor must be between 0 and 200.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return false;
    }

    const resp = await cqAPI.put('/settings/booking/provider/service_time_multiplier',{
      uid : employee_id,
      service_time_multiplier : num
    })
    if (resp.type !== 'ok'){
      return false
    }
    return true
  };

  const hasChanges = () => {
    return _.isEqual(originalSettings, settings);
  };

  const getValue = (path) =>
    path.reduce((acc, cur) => {
      return acc[cur];
    }, settings);

  const saveChanges = async () => {
    // You will need to define the API endpoint and payload structure as per your backend specifications
    setIsSaving(true);
    const changes = [];
    const errors = [];
    const deletes = [];
    const providerServices = [];
    const providerNewPatientOperatories = [];
    const providerExistingPatientOperatories = [];
    const providerPMSProviderIds = [];

    //checking if operatories are used for different providers on the same day
    const employess = settings.employees.filter((item) => item.email);
    let operatoryDaysMap = {};
    for (const e of employess) {
      //combining operatories
      const opIds = [
        ...new Set([
          ...(e.provider_info[bid]?.existing_patient_operatory_ids ||
            []),
          ...(e.provider_info[bid]?.new_patient_operatory_ids || []),
        ]),
      ];
      //getting the days that provider works on those operatories
      const days = Object.keys(e.providerWorkingHours[settings.root_id]);

      for (const opId of opIds) {
        if (!operatoryDaysMap[opId]) {
          operatoryDaysMap[opId] = new Set();
        }
        //check if there is any overlap
        const overlap = days.filter((day) => operatoryDaysMap[opId].has(day));
        if (overlap.length > 0) {
          const opName = settings.root_operatories.find((item) => item.id === opId)
            .name;
          toast({
            title: "Error",
            description: `${opName} couldn't be assigned to ${e.name} because it is already assigned for that time!`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          setIsSaving(false);
          return;
        }
        // continue if there is no overlap yet
        for (const day of days) {
          operatoryDaysMap[opId].add(day);
        }
      }
    }

    diff(originalSettings, settings).forEach((change) => {
      let path = change.path;
      if (typeof change.path[change.path.length - 1] === "number") {
        path = change.path.slice(0, -1);
      } else {
        path = change.path;
      }
      const newValue = getValue(path);
      if (
        change.kind !== "D" &&
        (path[2] === "providerLunchHours" || path[2] === "providerWorkingHours")
      ) {
        if (
          (newValue[0] === -1 || newValue[1] === -1) &&
          (newValue[0] * newValue[1] < 0 || path[2] === "providerWorkingHours")
        ) {
          errors.push(
            path[2] === "providerLunchHours"
              ? "Either do not set lunch hour or set both from and to values."
              : "Either do not have a provider for a day or have both from and to times for which they work."
          );
          return;
        }
        if (path[2] === "providerWorkingHours" && !newValue.length) {
          errors.push(
            "Either do not have a provider for a day or have both from and to times for which they work."
          );
          return;
        }
        if (settings.employees[path[1]].email){
          changes.push({
            pid: settings.employees[path[1]]._id,
            day: path[path.length - 1],
            bid: path[3],
            ...(path[2] === "providerLunchHours"
              ? {
                  lunch: newValue,
                }
              : {
                  hours: newValue,
                }),
          });
        }
      } else {
        if (change.kind === "D") {
          if (
            !deletes.find(
              (x) =>
                x.pid === settings.employees[path[1]]._id &&
                x.day === path[path.length - 1] &&
                x.bid === path[3]
            )
          ) {
            if (settings.employees[path[1]].email){
              deletes.push({
                pid: settings.employees[path[1]]._id,
                day: path[path.length - 1],
                bid: path[3],
              });
            }
          }
        } else {
          if (
            (path[4] === "service_id" || newValue.service_id) &&
            !providerServices.find(
              (x) =>
                x.pid === settings.employees[path[1]]._id &&
                _.isEqual(x.service_id, newValue.service_id || newValue)
            )
          ) {
            if (settings.employees[path[1]].email){
              providerServices.push({
                pid: settings.employees[path[1]]?._id || "",
                service_id: newValue.service_id || newValue,
              });
            }
          }
          if (
            (path[4] === "new_patient_operatory_ids" ||
              newValue.new_patient_operatory_ids) &&
            !providerNewPatientOperatories.find(
              (x) =>
                x.pid === settings.employees[path[1]]._id &&
                _.isEqual(
                  x.new_patient_operatory_ids,
                  newValue.new_patient_operatory_ids || newValue
                )
            )
          ) {
            if (settings.employees[path[1]].email){
              providerNewPatientOperatories.push({
                pid: settings.employees[path[1]]?._id || "",
                new_patient_operatory_ids:
                  newValue.new_patient_operatory_ids || newValue,
              });
            }
          }

          if (
            (path[4] === "existing_patient_operatory_ids" ||
              newValue.existing_patient_operatory_ids) &&
            !providerExistingPatientOperatories.find(
              (x) =>
                x.pid === settings.employees[path[1]]._id &&
                _.isEqual(
                  x.existing_patient_operatory_ids,
                  newValue.existing_patient_operatory_ids || newValue
                )
            )
          ) {
            if (settings.employees[path[1]].email){
              providerExistingPatientOperatories.push({
                pid: settings.employees[path[1]]?._id || "",
                existing_patient_operatory_ids:
                  newValue.existing_patient_operatory_ids || newValue,
              });
            }
          }

          if (
            (path[4] === "pms_provider_id" || newValue.pms_provider_id) &&
            !providerPMSProviderIds.find(
              (x) =>
                x.pid === settings.employees[path[1]]._id &&
                _.isEqual(x.pms_provider_id, newValue.pms_provider_id || newValue)
            )
          ) {
            if (settings.employees[path[1]].email){
              providerPMSProviderIds.push({
                pid: settings.employees[path[1]]?._id || "",
                pms_provider_id: newValue.pms_provider_id || newValue,
                bid: path[3],
              });
            }
          }
        }
      }
    });
    if (errors.length) {
      toast({
        title: "Error",
        description: errors[0],
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setIsSaving(false);
      return;
    }

    if (providerServices.length) {
      const result = await cqAPI.put(
        "/settings/booking/provider/services",
        providerServices
      );
      if (result.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: result.msg,
          header: "Error",
        });
        setIsSaving(false);
        return;
      }
    }

    if (
      providerNewPatientOperatories.length ||
      providerExistingPatientOperatories.length
    ) {
      // merging new and exisiting patient operatory rooms into a single object
      const operatories = [
        ...providerNewPatientOperatories,
        ...providerExistingPatientOperatories,
      ].reduce((acc, cur) => {
        const existingItem = acc.find((item) => item.pid === cur.pid);
        if (!existingItem) {
          acc.push({
            ...cur,
          });
        } else {
          // Merge each property (except pid) from cur into existingItem
          Object.entries(cur).forEach(([key, value]) => {
            if (key === "pid") return;
            if (Array.isArray(value)) {
              existingItem[key] = existingItem[key]
                ? existingItem[key].concat(value)
                : [...value];
            } else {
              existingItem[key] = value;
            }
          });
        }
        return acc;
      }, []);

      const result = await cqAPI.put(
        "/settings/booking/provider/operatory",
        operatories
      );
      if (result.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: result.msg,
          header: "Error",
        });
        setIsSaving(false);
        return;
      }
    }

    if (providerPMSProviderIds.length) {
      const result = await cqAPI.put(
        "/settings/booking/provider/pms_provider_id",
        providerPMSProviderIds
      );
      if (result.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: result.msg,
          header: "Error",
        });
        setIsSaving(false);
        return;
      }
    }

    if (changes.length) {
      const result = await cqAPI.put("/settings/booking/provider", changes);
      if (result.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: result.msg,
          header: "Error",
        });
        setIsSaving(false);
        return;
      }
    }

    if (deletes.length) {
      const result = await cqAPI.del("/settings/booking/provider", deletes);
      if (result.msg) {
        dialogManager.open({
          onDeny: () => {},
          acceptText: "OK",
          body: result.msg,
          header: "Error",
        });
        setIsSaving(false);
        return;
      }
    }
    await fetchSettings();
    setIsSaving(false);
  };

  // Original function that *can* return an empty object
  const getProviderHoursForDay = (providerWorkingHours, dayIndex) => {
    const bid = Object.keys(providerWorkingHours).find(
      (b) => b[String(dayIndex)] && b[String(dayIndex)].length
    );
    if (!bid) {
      return {};
    }
    return { hours: providerWorkingHours[bid][String(dayIndex)], bid };
  };

  const updateStaff = (settings) => setStaff(staff.map(s=>{
    const employee = settings.employees.find(e=>s._id === e._id)
    if (!employee){
      return s
    }
    return employee
  }));

  const updateProviderBusinessForDay = (_employee, dayIndex, _bid) => {
    const newSettings = {
      ...settings,
      employees: settings.employees.map((employee) => {
        if (employee._id && employee._id === _employee._id) {
          const { bid } = getProviderHoursForDay(
            employee.providerWorkingHours,
            dayIndex
          );
          if (!_bid) {
            if (bid) {
              delete employee.providerWorkingHours[bid][String(dayIndex)];
              delete employee.providerLunchHours[bid][String(dayIndex)];
            } else {
              //should not happen
            }
          } else {
            if (bid) {
              if (bid !== _bid) {
                employee.providerWorkingHours[_bid][String(dayIndex)] =
                  employee.providerWorkingHours[bid][String(dayIndex)];
                delete employee.providerWorkingHours[bid][String(dayIndex)];
                delete employee.providerLunchHours[bid][String(dayIndex)];
              } else {
                employee.providerWorkingHours[_bid] =
                employee.providerWorkingHours[_bid] || {};
                employee.providerLunchHours[_bid] =
                employee.providerLunchHours[_bid] || {};
                employee.providerWorkingHours[_bid][String(dayIndex)] = [-1, -1];
                employee.providerLunchHours[_bid][String(dayIndex)] = [-1, -1];
              }
            } else {
              employee.providerWorkingHours[_bid] =
                employee.providerWorkingHours[_bid] || {};
              employee.providerLunchHours[_bid] =
                employee.providerLunchHours[_bid] || {};
              employee.providerWorkingHours[_bid][String(dayIndex)] = [-1, 1];
              employee.providerLunchHours[_bid][String(dayIndex)] = [-1, 1];
            }
          }
        }
        return employee;
      }),
    };
    updateStaff(newSettings);
    setSettings(newSettings);
  };

  const updateProviderLunchForDay = (_employee, dayIndex, hourIndex, value) => {
    const newSettings = {
      ...settings,
      employees: settings.employees.map((employee) => {
        if (employee._id && employee._id === _employee._id) {
          const { bid } = getProviderHoursForDay(
            employee.providerLunchHours,
            dayIndex
          );
          employee.providerLunchHours[bid][String(dayIndex)] =
            employee.providerLunchHours[bid][String(dayIndex)] || [-1, -1];
          employee.providerLunchHours[bid][String(dayIndex)][hourIndex] =
            parseFloat(value);
        }
        return employee;
      }),
    };
    updateStaff(newSettings);
    setSettings(newSettings);
  };

  const updateProviderHoursForDay = (_employee, dayIndex, hourIndex, value) => {
    const newSettings = {
      ...settings,
      employees: settings.employees.map((employee) => {
        if (employee._id && employee._id === _employee._id) {
          const { hours, bid } = getProviderHoursForDay(
            employee.providerWorkingHours,
            dayIndex
          );
          employee.providerWorkingHours[bid][String(dayIndex)] =
            employee.providerWorkingHours[bid][String(dayIndex)] || [-1, -1];
          employee.providerWorkingHours[bid][String(dayIndex)][hourIndex] =
            parseFloat(value);
        }
        return employee;
      }),
    };
    updateStaff(newSettings);
    setSettings(newSettings);
  };

  // Options updating (unchanged)
  const bgColor = useColorModeValue("gray.100", "gray.700");
  const updateName = async (employee_id, name) => {
    const resp = await cqAPI.put('/settings/booking/provider/name',{
      uid : employee_id,
      name
    })
    if (resp.type !== 'ok'){
      return false
    }
    return true
  }

  const bid = getBid();
  const getProviderName = (prov) => {
    const fullName = `${prov.first_name ?? ""} ${prov.last_name ?? ""}`.trim();
    const name = fullName || (prov.name?.trim() || "No name");

    if (prov.specialty) {
      return `${prov.pms_id} ${name}, ${prov.specialty}`;
    }
    return `${prov.pms_id} ${name}`;
  };

  const getPrimaryProviderOptions = () => {
    if (!settings?.primary?.length) {
      return [];
    }
    return settings.primary.map((prov) => ({
      value: prov.pms_id,
      label: getProviderName(prov),
    }));
  };

  const getProviderOptions = () => {
    return (
      (settings?.raw_providers && settings?.raw_providers[bid]) || []
    ).map((prov) => ({
      value: prov.pms_id,
      label: getProviderName(prov),
    }));
  };
  
  const getAppointmentStatusOptions = () => {
    return (settings?.raw_appointment_statuses || []).map((ap) => ({
      value: ap.pms_id,
      label: ap.name,
    }));
  };

  const getTimeAdjustmentFactor = (employee) => {
    const factor =
      employee.provider_info &&
      settings.root_id &&
      employee.provider_info[settings.root_id] &&
    employee.provider_info[settings.root_id].service_time_multiplier;

    return typeof factor === "number" && !Number.isNaN(factor) ? factor : 0;
  };

  // ----------------------------------
  // Render
  // ----------------------------------
  return (
    <Box p={4} overflowY="auto" height="100%" width="100%">
      <Tabs
        isFitted
        variant="enclosed"
        isLazy
        lazyBehavior="unmount"
        padding="8px 24px 24px 24px"
        bgColor={colorMode === "light" ? "#FFFFFF" : "#212330"}
        borderRadius="16px"
      >
        <TabList mb="1em">
          <Tab
            padding="0px 16px 16px 16px"
            border="none"
            _selected={{
              borderBottom: `4px solid ${
                colorMode === "light"
                  ? theme.colors.brand[300]
                  : theme.colors.lightBackground
              }`,
            }}
          >
            Services
          </Tab>
          <Tab
            padding="0px 16px 16px 16px"
            border="none"
            _selected={{
              borderBottom: `4px solid ${
                colorMode === "light"
                  ? theme.colors.brand[300]
                  : theme.colors.lightBackground
              }`,
            }}
          >
            Providers Schedule
          </Tab>
          <Tab
            padding="0px 16px 16px 16px"
            border="none"
            _selected={{
              borderBottom: `4px solid ${
                colorMode === "light"
                  ? theme.colors.brand[300]
                  : theme.colors.lightBackground
              }`,
            }}
          >
            Options
          </Tab>
        </TabList>
        <TabPanels>
          {/* -------------------------- SERVICES TAB -------------------------- */}
          <TabPanel>
            {!isCloud9 && (<Flex justifyContent="flex-end" mb={4}>
              <Button leftIcon={<AddIcon />} onClick={onOpenNewService}>
                Add Service
              </Button>
            </Flex>)}

            <SimpleGrid
              templateColumns={{
                base: "minmax(100px,250px) 1fr minmax(100px,200px) 1fr ",
                md:   "100px 1fr 1fr 1fr ",
              }}
              gap={4} px={4} py={2} mb={2} ml={6}
              fontSize="sm"
              fontWeight="bold"
              padding="2px 2px 10px 0px"
              alignItems="center"
            >
              <Text justifySelf="start">Service Name</Text>
              <Text justifySelf="center">Duration (minutes)</Text>
              {!isCareStack && (<Text justifySelf="start">Appointment Type (mandatory)</Text>)}
            </SimpleGrid>


            {settings?.non_standard_services &&
              settings?.non_standard_services.map((service) => (
                <SimpleGrid
                  className="service"
                  key={service.id}
                  savable="true"
                  mb="2"
                  padding="16px 24px 16px 24px"
                  bgColor={bgColor}
                  borderRadius="md"
                  flexDirection={{ base: "column", md: "row" }}
                  alignItems={{ base: "flex-start", md: "center" }}
                >
                  <Flex flexWrap="wrap" display="flex">
                    <Flex minWidth="150px" width="250px" align="center">
                      <EditableText
                        initialValue={service.name}
                        onSave={(newValue) =>
                          updateService(service, "name", newValue.trim())
                        }
                        placeholder="Service Name"
                        pattern={/^.{3,}/}
                        invalid="more than 3 character."
                        editable={!/^([0123])$/.test(service.id)}
                      />
                    </Flex>
                    <Flex
                      minWidth="150px"
                      maxWidth="200px"
                      paddingLeft="40px"
                      align="center"
                    >
                      {!nonConfigurableServices.includes(service.id) && (
                        <EditableText
                          initialValue={service.duration}
                          onSave={(newValue) =>
                            updateService(service, "duration", parseInt(newValue))
                          }
                          editable={!isCloud9}
                          placeholder="Duration"
                          pattern={/^[0-9]{1,3}$/}
                          invalid="Numbers only (up to 3 digits)"
                        />
                      )}
                    </Flex>
                    <Flex pl="40px" justifyContent="flex-end" align="center">
                      <ReactSelect
                        placeholder="Appointment Type"
                        isDisabled={isCloud9}
                        styles={customStyles}
                        isMulti={!isCloud9}
                        options={(
                          (settings?.raw_appointment_types &&
                            settings?.raw_appointment_types[bid]) ||
                          []
                        ).map((apt) => ({ value: apt.pms_id, label: apt.name }))}
                        value={(
                          (settings?.raw_appointment_types &&
                            settings?.raw_appointment_types[bid]) ||
                          []
                        )
                          .filter((apt) =>
                            (service.appointment_types || []).includes(apt.pms_id)
                          )
                          .map((apt) => ({
                            value: apt.pms_id,
                            label: apt.name,
                          }))}
                        onChange={(e) => {
                          if (!isCloud9) {
                            handleAppointmentTypeChange(service, e);
                          }  else {
                            handleAppointmentTypeChange(service, [e]);
                          }
                        }}
                      />
                    </Flex>
                    {!nonConfigurablePatientTypeForServices.includes(
                      service.id
                    ) && (
                      <Flex flexGrow={1} justifyContent="center" align="center">
                        <RadioGroup
                          defaultValue={service.patient_type}
                          onChange={(e) =>
                            updateService(service, "patient_type", e)
                          }
                        >
                          <Stack direction="row" pt="1.25" pl="6.25">
                            <Radio value="n">New Patients</Radio>
                            <Radio value="e">Existing Patients</Radio>
                            <Radio value="ne">Both</Radio>
                          </Stack>
                        </RadioGroup>
                      </Flex>
                    )}
                    {(isDentrix || isCloud9) &&
                      requiresPrimaryProvider.includes(service.id) && (
                        <Flex flexGrow={1} justifyContent="center" align="left">
                          <ReactSelect
                            placeholder="Primary Provider"
                            styles={customStyles}
                            options={getPrimaryProviderOptions()}
                            value={getPrimaryProviderOptions().filter(
                              (prov) =>
                                service.primary_provider_pms_id &&
                                service.primary_provider_pms_id === prov.value
                            )}
                            onChange={(e) =>
                              updateService(
                                service,
                                "primary_provider_pms_id",
                                e.value
                              )
                            }
                          />
                        </Flex>
                      )}
                    <Flex ml="auto" pr="20px" align="center">
                      {!nonConfigurableServices.includes(service.id) && (
                        <>
                          <Switch
                            isChecked={service.enabled}
                            float="right"
                            pt="1.25"
                            pl="6.25"
                            onChange={(e) =>
                              handleToggleEnabled(service, e.target.checked)
                            }
                          />
                          <Text ml={2}>Enabled</Text>
                        </>
                      )}
                    </Flex>
                    {!isCloud9 && (<Flex minWidth="40px" align="center">
                      {!["0", "1", "2", "3"].includes(service.id) && (
                        <IconButton
                          aria-label="Delete service"
                          icon={<DeleteIcon />}
                          size="sm"
                          onClick={() => deleteService(service.id)}
                          ml="2"
                          colorScheme="red"
                        />
                      )}
                      </Flex>)}

                    <Flex width="100%" mt={2} align="center">
                      <Box >
                        <Button
                          size="sm"
                          padding={0}
                          variant="ghost"
                          rightIcon={
                            advancedOpen[service.id] ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          }
                          onClick={() => toggleAdvanced(service.id)}
                        >
                          Advanced
                        </Button>
                      </Box>
                    </Flex>

                    {advancedOpen[service.id] && (
                      <Flex
                        width="100%"
                        mt={2}
                        align="center"
                        flexWrap="wrap"
                        gap={2}
                      >
                        <Box mr="4">
                          <Text fontWeight="bold">Appointment Notes: </Text>
                        </Box>
                        <Flex align="center" width="100%">
                          <EditableText
                            initialValue={service.appointment_notes || ""}
                            onSave={(newValue) =>
                              updateService(service, "appointment_notes", newValue)
                            }
                            placeholder="Optional appointment notes"
                          />
                        </Flex>
                      </Flex>
                    )}
                    {advancedOpen[service.id] && (
                      <Flex
                        width="100%"
                        mt={2}
                        align="center"
                        flexWrap="wrap"
                        gap={2}
                      >
                        <Box mr="4">
                          <Text fontWeight="bold">Description: </Text>
                        </Box>
                        <Flex align="center" width="100%">
                          <EditableText
                            initialValue={service.description || ""}
                            onSave={(newValue) =>
                              updateService(service, "description", newValue)
                            }
                            placeholder="Optional description"
                          />
                        </Flex>
                      </Flex>
                    )}
                  </Flex>
                </SimpleGrid>
              ))}
          </TabPanel>

          {/* -------------------------- PROVIDERS SCHEDULE TAB -------------------------- */}
          <TabPanel>
            <Flex justifyContent="flex-end" mb={4}>
              <Button
                leftIcon={<CheckIcon />}
                onClick={saveChanges}
                isDisabled={hasChanges() || isSaving}
                isLoading={isSaving}
                loadingText="Saving..."
                spinner={<Spinner size="sm" />}
              >
                Save Changes
              </Button>
            </Flex>
            <SimpleGrid columns={{ base: 1, md: 1, lg: 1 }} spacing="20px">
              {staff.map((employee, index) => {
                if (!employee._id) return null;

                const isDisabledFields = !employee.email;

                return (
                  <Box
                    key={index}
                    borderWidth="1px"
                    borderRadius="lg"
                    mb={4}
                    width="100%"
                    paddingBottom="15px"
                  >
                    <Box
                      bg={employee.email ? "blue.500" : "gray.500"}
                      color="white"
                      p={4}
                      borderTopRadius="lg"
                    >
                      <Flex justifyContent="space-between">
                        <Flex direction="column" width="100%">
                  <Flex mb={1} align="flex-start">
                    {/* Left side: name, hired date, time adjustment factor */}
                    <Box>
                      {employee.email ? (
                        <Stack spacing={1} align="flex-start">
                          <Flex align="center"><EditableText
                            onSave={(newValue) => updateName(employee._id, newValue)}
                            initialValue={employee.name}
                            placeholder="Name"
                            pattern={/[a-zA-Z]{1}.$/}
                            background={"blue.500"}
                            invalid="Please enter a valid name"
                          /></Flex>
                          <Text fontSize="sm">
                            Hired: {new Date(employee.created).toLocaleDateString()}
                          </Text>
                          <Flex align="center" gap={2}>
                            <Text fontSize="sm">Time Adjustment Factor:</Text>
                              <Flex align="center"><EditableText
                                onSave={(newValue) =>
                                  handleTimeAdjustmentFactorChange(employee._id, newValue)
                                }
                                background={"blue.500"}
                                initialValue={getTimeAdjustmentFactor(employee)}
                                placeholder="0"
                                pattern={/^\d{1,3}$/}
                                invalid="Number between 0 and 200"
                              /></Flex>
                          </Flex>
                        </Stack>
                      ) : (
                        <Stack spacing={1} align="flex-start">
                          <Text fontWeight="bold">{employee.name || "No name"}</Text>
                          <Flex align="center" gap={2}>
                            <Text fontSize="sm">Time Adjustment Factor:</Text>
                              <Flex align="center"><EditableText
                                onSave={(newValue) =>
                                  handleTimeAdjustmentFactorChange(employee._id, newValue)
                                }
                                initialValue={getTimeAdjustmentFactor(employee)}
                                placeholder="0"
                                pattern={/^-?\d{1,3}$/}
                                invalid="Number between 0 and 200"
                              />
                            </Flex>
                          </Flex>
                        </Stack>
                      )}
                    </Box>

                    {/* Right side: Provider select */}
                    <Spacer />
                    <Flex justifyContent="flex-end" gap={8}>
                      Provider:
                      <ReactSelect
                        isDisabled={isDisabledFields}
                        onChange={(e) => handlePMSProviderChange(employee, e.value)}
                        styles={customStyles}
                        options={getProviderOptions()}
                        value={getProviderOptions().find(
                          (provider) =>
                            provider.value ===
                            employee?.provider_info[settings.root_id]?.pms_provider_id
                        )}
                      />
                    </Flex>
                  </Flex>
                          
                          <Flex justifyContent="flex-end" p={1} gap={8}>
                            Services:
                            <ReactSelect
                              isDisabled={isDisabledFields}
                              onChange={(e) => {
                                handleServiceChange(
                                  employee,
                                  e.map((option) => option.value)
                                );
                              }}
                              styles={customStyles}
                              isMulti
                              options={settings.root_services.map((service) => ({
                                value: service.id,
                                label: service.name,
                              }))}
                              value={(
                                employee.provider_info &&
                                settings.root_id &&
                                employee.provider_info[settings.root_id] &&
                                employee.provider_info[settings.root_id]
                                  .service_id
                              )
                                ?.map((sid) => {
                                  const s = settings.root_services.find(
                                    (i) => i.id === sid
                                  );
                                  if (!s) {
                                    return null;
                                  }
                                  return {
                                    value: s.id,
                                    label: s.name,
                                  };
                                })
                                .filter((x) => !!x)}
                            />
                          </Flex>

                          <Flex justifyContent="flex-end" p={1} gap={8}>
                            New Patient Operatories:
                            <ReactSelect
                              isDisabled={isDisabledFields}
                              onChange={(e) => {
                                handleOperatoryChange(
                                  employee,
                                  true,
                                  e.map((option) => option.value)
                                );
                              }}
                              styles={customStyles}
                              isMulti
                              options={settings.root_operatories.map(
                                (operatory) => ({
                                  value: operatory.id,
                                  label: `${operatory.name}: ${operatory.id}`,
                                })
                              )}
                              value={(
                                employee.provider_info &&
                                employee.provider_info[bid] &&
                                employee.provider_info[bid]
                                  .new_patient_operatory_ids
                              )
                                ?.map((sid) => {
                                  const s = settings.root_operatories.find(
                                    (i) => i.id === sid
                                  );
                                  if (!s) {
                                    return null;
                                  }
                                  return {
                                    value: s.id,
                                    label: `${s.name}: ${s.id}`
                                  };
                                })
                                .filter((x) => !!x)}
                            />
                          </Flex>

                          <Flex justifyContent="flex-end" p={1} gap={8}>
                            Existing Patient Operatories:
                            <ReactSelect
                              isDisabled={isDisabledFields}
                              onChange={(e) => {
                                handleOperatoryChange(
                                  employee,
                                  false,
                                  e.map((option) => option.value)
                                );
                              }}
                              styles={customStyles}
                              isMulti
                              options={settings.root_operatories.map(
                                (operatory) => ({
                                  value: operatory.id,
                                  label: `${operatory.name}: ${operatory.id}`
                                })
                              )}
                              value={(
                                employee.provider_info &&
                                employee.provider_info[bid] &&
                                employee.provider_info[bid]
                                  .existing_patient_operatory_ids
                              )
                                ?.map((sid) => {
                                  const s = settings.root_operatories.find(
                                    (i) => i.id === sid
                                  );
                                  if (!s) {
                                    return null;
                                  }
                                  return {
                                    value: s.id,
                                    label: `${s.name}: ${s.id}`
                                  };
                                })
                                .filter((x) => !!x)}
                            />
                          </Flex>
                        </Flex>
                      </Flex>
                    </Box>

                    {/* If there's no email, show the "Activate" button */}
                    {!employee.email && (
                      <Flex p={4} justifyContent="flex-end">
                        <Button
                          colorScheme="teal"
                          onClick={() => handleActivateClick(employee)}
                        >
                          Activate
                        </Button>
                      </Flex>
                    )}

                    {/* If isDenticon or Dentrix Ascend, show a doc link on how to set up scheduling in the PMS; otherwise show scheduling fields */}
                    {!isDenticon && !isDentrixAscend && !isCloud9 &&
                      employee._id &&
                      days.map((day, dayIndex) => {
                        const { hours, bid } = getProviderHoursForDay(
                          employee.providerWorkingHours,
                          dayIndex
                        );
                        const lunch = getProviderHoursForDay(
                          employee.providerLunchHours || {},
                          dayIndex
                        );

                        return (
                          <Flex key={dayIndex} mt={2} px={4} alignItems="center">
                            <Text minWidth="50px">{day}</Text>
                            <Select
                              width="300px"
                              placeholder="Select business"
                              value={hours && bid}
                              isDisabled={isDisabledFields}
                              onChange={(e) =>
                                updateProviderBusinessForDay(
                                  employee,
                                  dayIndex,
                                  e.target.value
                                )
                              }
                            >
                              {settings.businesses.map((business) => (
                                <option key={business.id} value={business.id}>
                                  {business.name}
                                </option>
                              ))}
                            </Select>
                            <Text paddingLeft="10px">From:</Text>
                            <Select
                              ml="10px"
                              isDisabled={isDisabledFields || !bid || !hours}
                              width="85px"
                              value={(hours && hours[0]) || -1}
                              onChange={(e) =>
                                updateProviderHoursForDay(
                                  employee,
                                  dayIndex,
                                  0,
                                  e.target.value
                                )
                              }
                            >
                              <option value={-1}>Select</option>
                              {Hours.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              ))}
                            </Select>
                            <Text paddingLeft="10px">To:</Text>
                            <Select
                              ml="10px"
                              isDisabled={isDisabledFields || !bid || !hours}
                              width="85px"
                              value={(hours && hours[1]) || -1}
                              onChange={(e) =>
                                updateProviderHoursForDay(
                                  employee,
                                  dayIndex,
                                  1,
                                  e.target.value
                                )
                              }
                            >
                              <option value={-1}>Select</option>
                              {Hours.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              ))}
                            </Select>
                            <Text paddingLeft="10px">Lunch from:</Text>
                            <Select
                              ml="10px"
                              isDisabled={isDisabledFields || !bid || !hours}
                              width="85px"
                              value={(lunch.hours && lunch.hours[0]) || -1}
                              onChange={(e) =>
                                updateProviderLunchForDay(
                                  employee,
                                  dayIndex,
                                  0,
                                  e.target.value
                                )
                              }
                            >
                              <option value={-1}>Select</option>
                              {Hours.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              ))}
                            </Select>
                            <Text paddingLeft="10px">To:</Text>
                            <Select
                              ml="10px"
                              isDisabled={isDisabledFields || !bid || !hours}
                              width="85px"
                              value={(lunch.hours && lunch.hours[1]) || -1}
                              onChange={(e) =>
                                updateProviderLunchForDay(
                                  employee,
                                  dayIndex,
                                  1,
                                  e.target.value
                                )
                              }
                            >
                              <option value={-1}>Select</option>
                              {Hours.map((hour) => (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              ))}
                            </Select>
                          </Flex>
                        );
                      })}
                    {isDenticon && employee._id && (
                      <Link
                        href="https://support.planetdds.com/hc/en-us/articles/360045117292-How-do-we-add-or-modify-schedule-templates-for-providers"
                        target="_blank"
                      >
                        <br />
                        Click Here for more info on how to update the schedule!
                        <ExternalLinkIcon mx="2px" />
                      </Link>
                    )}
                    {isDentrixAscend && employee._id && (
                      <Link
                        href="https://hsps.pro/DentrixAscend/Help/Editing_the_time_blocks_of_a_scheduling_template.htm"
                        target="_blank"
                      >
                        <br />
                        Click Here for more info on how to update the scheduling template!
                        <ExternalLinkIcon mx="2px" />
                      </Link>
                    )}
                  </Box>
                );
              })}
            </SimpleGrid>
          </TabPanel>

          {/* -------------------------- OPTIONS TAB -------------------------- */}
          <TabPanel>
            <Stack spacing={4}>
            {!isCloud9 && (<Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Text minWidth="300px">
                  Initial exams include a subsequent hygiene visit
                </Text>
                <Checkbox
                  bg="white"
                  isChecked={settings?.options?.new_patient_followed_by_hygiene}
                  onChange={(e) =>{
                    const cleaningService = (settings?.root_services || []).find(s => s.id === '3' && s.name === 'Cleaning');
                    if (e.target.checked && cleaningService && (!cleaningService.enabled || !['ne', 'n'].includes(cleaningService.patient_type))) {
                      e.preventDefault();
                      toast({
                        title: "Error",
                        description: 'To enable this feature, first enable Cleanings for New Patients or Both on the Services tab.',
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                      return;
                    }
                    updateOption(
                      "new_patient_followed_by_hygiene",
                      e.target.checked
                    );
                  }}
                />
              </Flex>)}
              <Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Text minWidth="300px">
                  Overlap Ratio: Set above 100 to permit overlapping
                  appointments, such as 200 for full double-booking.
                </Text>
                <Flex width="150px" align="center">
                  <EditableText
                    onSave={(newValue) =>
                      updateOption("overlap_ratio", parseInt(newValue))
                    }
                    initialValue={settings?.options?.overlap_ratio}
                    placeholder="Overlap Ratio"
                    pattern={/^\d{1,3}$/}
                    invalid="more than 3 character."
                  />
                </Flex>
              </Flex>
                  <Flex
                    align="center"
                    justify="space-between"
                    bgColor={bgColor}
                    padding="20px 10px"
                    borderRadius="md"
                  >
                    <Text minWidth="300px">
                      The number of days in the future for which we should show availabilities for.
                    </Text>
                    <Flex width="150px" align="center">
                      <EditableText
                        onSave={(newValue) =>
                          updateOption("show_appointments_days_in_future", parseInt(newValue))
                        }
                        initialValue={settings?.show_appointments_days_in_future}
                        placeholder="Days"
                        pattern={/^\d{1,3}$/}
                        invalid="more than 3 character."
                      />
                    </Flex>
                  </Flex>
                      <Flex
                        align="center"
                        justify="space-between"
                        bgColor={bgColor}
                        padding="20px 10px"
                        borderRadius="md"
                      >
                        <Text minWidth="150px">
                          Days Until First Availability
                        </Text>
                        <Flex width="150px" align="center">
                          <EditableText
                            onSave={(newValue) =>
                              updateOption("skip_appointments_days", parseInt(newValue))
                            }
                            initialValue={settings?.skip_appointments_days}
                            placeholder="Days"
                            pattern={/^\d{1,3}$/}
                            invalid="more than 3 character."
                          />
                        </Flex>
                      </Flex>
              <Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Text minWidth="300px" textAlign="left">
                  Signup Message: Displayed to new patients when booking their
                  appointment.
                </Text>
                <Flex width="300px" align="center">
                  <EditableText
                    onSave={(newValue) => updateOption("ols_message", newValue)}
                    initialValue={settings?.options?.ols_message}
                    placeholder="Signup Message"
                  />
                </Flex>
              </Flex>

              <Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Text minWidth="300px" textAlign="left">Excluded Operatories</Text>
                <Box minW="300px">
                  <ReactSelect
                    isMulti
                    styles={customStyles}
                    placeholder="Select operatories to exclude"
                    options={(settings?.root_operatories || []).map((operatory) => ({
                      value: operatory.id,
                      label: `${operatory.name} : ${operatory.id}`,
                    }))}
                    value={(settings?.options?.excluded_ops || [])
                      .map((id) => {
                        const op = (settings?.root_operatories || []).find((o) => o.id === id);
                        return op ? { value: op.id, label: `${op.name} : ${op.id}` } : null;
                      })
                      .filter(Boolean)}
                    onChange={(selected) =>
                      updateOption(
                        "excluded_ops",
                        (selected || []).map((opt) => opt.value)
                      )
                    }
                  />
                </Box>
              </Flex>
              <Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Text minWidth="300px" textAlign="left">Apply Appointment blocks</Text>
                <Checkbox
                  bg="white"
                  isChecked={settings?.options?.count_op_notes_as_blocked}
                  onChange={(e) =>
                    updateOption("ols.count_op_notes_as_blocked", e.target.checked)
                  }
                />
              </Flex>

              <Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Text minWidth="300px" textAlign="left">Apply Appointment notes</Text>
                <Checkbox
                  bg="white"
                  isChecked={settings?.options?.count_op_blocks_as_blocked}
                  onChange={(e) =>
                    updateOption("ols.count_op_blocks_as_blocked", e.target.checked)
                  }
                />
              </Flex>
              <Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Tooltip
                hasArrow
                placement="top"
                label={`Patients can only reschedule if their provider is enabled in the "Providers Schedule" tab. Enabling this option lets patients reschedule with any provider, using PMS data to estimate schedules. However, we recommend adding providers individually for accuracy.`}
                >
                <Text minWidth="300px" textAlign="left" cursor="help">
                  Allow Rescheduling for All Providers (?)
                </Text>
                </Tooltip>
                <Checkbox
                  bg="white"
                  isChecked={settings?.options?.allow_rescheduling_for_all_providers}
                  onChange={(e) =>
                    updateOption("ols.allow_rescheduling_for_all_providers", e.target.checked)
                  }
                />
              </Flex>
              <Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Text minWidth="300px" textAlign="left">Display booked appointments under primary provider</Text>
                <Checkbox
                  bg="white"
                  isChecked={settings?.options?.display_appointments_under_primary_provider}
                  onChange={(e) =>
                    updateOption("ols.display_appointments_under_primary_provider", e.target.checked)
                  }
                />
              </Flex>
              {isCareStack && (<Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Tooltip
                  hasArrow
                  placement="top"
                  label={`Set the appointment status from your PMS that Viva should use to mark appointments as Confirmed`}
                >
                  <Text minWidth="300px" textAlign="left" cursor="help">
                    Confirmed appointment status
                  </Text>
                </Tooltip>
                <Box minW="300px">
                  <ReactSelect
                    styles={customStyles}
                    placeholder="Select confirmed status"
                    options={getAppointmentStatusOptions()}
                    value={getAppointmentStatusOptions().filter(
                      ap => ap.value === settings?.options?.confirmed_appointment_pms_status
                    )}
                    onChange={(e) =>
                      updateOption(
                        "ols.confirmed_appointment_pms_status",
                        e.value
                      )
                    }
                  />
                </Box>
              </Flex>)}
              {isCareStack && (<Flex
                align="center"
                justify="space-between"
                bgColor={bgColor}
                padding="20px 10px"
                borderRadius="md"
              >
                <Tooltip
                  hasArrow
                  placement="top"
                  label={`Set the appointment status from your PMS that Viva should use to mark appointments as Not Confirmed`}
                >
                  <Text minWidth="300px" textAlign="left" cursor="help">
                    Not Confirmed appointment status
                  </Text>
                </Tooltip>
                <Box minW="300px">
                  <ReactSelect
                    styles={customStyles}
                    placeholder="Select confirmed status"
                    options={getAppointmentStatusOptions()}
                    value={getAppointmentStatusOptions().filter(
                      ap => ap.value === settings?.options?.unconfirmed_appointment_pms_status
                    )}
                    onChange={(e) =>
                      updateOption(
                        "ols.unconfirmed_appointment_pms_status",
                        e.value
                      )
                    }
                  />
                </Box>
              </Flex>)}
            </Stack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* -------------------------- ADD SERVICE MODAL -------------------------- */}
      <Modal isOpen={isNewServiceModalOpen} onClose={onCloseNewService}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add a New Service</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Input
                placeholder="Service Name"
                value={newService.name}
                onChange={(e) => handleNewServiceChange("name", e.target.value)}
              />
              <Input
                placeholder="Duration (in minutes)"
                type="number"
                value={newService.duration}
                onChange={(e) =>
                  handleNewServiceChange("duration", parseInt(e.target.value))
                }
              />
              <RadioGroup
                onChange={(value) => handleNewServiceChange("patient_type", value)}
                value={newService.patient_type}
              >
                <Stack direction="row">
                  <Radio value="n">New Patients</Radio>
                  <Radio value="e">Existing Patients</Radio>
                  <Radio value="ne">Both</Radio>
                </Stack>
              </RadioGroup>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={saveNewService}>
              Save
            </Button>
            <Button variant="secondary" onClick={onCloseNewService}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* -------------------------- ACTIVATE PROVIDER MODAL -------------------------- */}
      <ActivateProviderModal
        isOpen={isActivateModalOpen}
        employee={activateEmployee}
        onClose={() => setIsActivateModalOpen(false)}
        onActivate={handleActivateProviderDirect}
        loading={loading}
      />
    </Box>
  );
};

// Memoized child modal to avoid re-rendering the whole page while typing
const ActivateProviderModal = React.memo(function ActivateProviderModal({ isOpen, onClose, employee, onActivate, loading }) {
  const toast = useToast();
  const [name, setName] = React.useState(employee?.name || "");
  const [email, setEmail] = React.useState("");
  const [doesNotNeedLogin, setDoesNotNeedLogin] = React.useState(false);

  React.useEffect(() => {
    if (employee) {
      setName(employee.name || "");
      setEmail("");
      setDoesNotNeedLogin(false);
    }
  }, [employee, isOpen]);

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleActivate = async () => {
    if (!name.trim()) {
      toast({
        title: "Error",
        description: "Provider name is required.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (!doesNotNeedLogin && !isValidEmail(email)) {
      toast({
        title: "Error",
        description: "You must enter a valid email address.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    await onActivate({ name, email, doesNotNeedLogin });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Activate Provider</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Input
              placeholder="Provider name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              isInvalid={!name.trim()}
            />
            <Checkbox
              isChecked={doesNotNeedLogin}
              onChange={(e) => {
                const v = e.target.checked;
                setDoesNotNeedLogin(v);
                if (v) setEmail("");
              }}
            >
              This provider does not need to login
            </Checkbox>
            <Input
              placeholder="Email address for this provider"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              isDisabled={doesNotNeedLogin}
            />
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
              <Button
              colorScheme="blue"
              onClick={handleActivate}
              isLoading={loading}
              loadingText="Activating..."
              spinner={<Spinner size="sm" />}
              isDisabled={loading}
              >
            Activate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

export default BookingSettings;
